import type { ChangeEvent, FC, KeyboardEvent } from 'react';
import { useEffect, useRef, useState } from 'react';

import type { InputProps } from '@cognite/cogs.js-v11';
import { Body } from '@cognite/cogs-core-v11/tokens/typography/typography.tsx';
import { Flex } from '@cognite/cogs-core-v11/components/flex/flex.tsx';
import { Input } from '@cognite/cogs-core-v11/components/input/input.tsx';

import { useUpsertAppConfigMutation } from '../../../hooks/use-mutation/use-upsert-app-config-mutation/use-upsert-app-config-mutation';
import { useAppConfigQuery } from '../../../hooks/use-query/use-app-config-query';
import { useSpaceQuery } from '../../../hooks/use-query/use-space-query';
import { SaveConfigurationButton } from '../../../save-configuration/save-configuration-button';
import type { AppConfig } from '../../../types';
import { getIsNewConfigVersion } from '../../../utils/utils';
import * as S from '../elements';

import { MigrationConfirmationContent } from './migration-confirmation-content';

interface Props {
  dataType: 'app' | 'source';
  hasAppConfigPermission: boolean;
  rootLocationExternalId: string;
}

export const RootLocationConfigSpace: FC<Props> = ({
  dataType,
  hasAppConfigPermission,
  rootLocationExternalId,
}: Props) => {
  const ref = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState<string>('');
  const [instanceSpace, setInstanceSpace] = useState<string>('');
  const [inputStatus, setInputStatus] =
    useState<InputProps['status']>(undefined);
  const [inputStatusText, setInputStatusText] = useState<string>('');
  const [enableValidation, setEnableValidation] = useState<boolean>(false);

  const { data: spaces = [], status } = useSpaceQuery(
    inputValue,
    enableValidation
  );
  const { data: appConfig, isSuccess, isLoading } = useAppConfigQuery();
  const isNewConfigVersion = getIsNewConfigVersion(appConfig);
  const { mutateAsync: upsertAppConfig } = useUpsertAppConfigMutation();

  const configuredRootLocation =
    appConfig?.featureConfiguration?.rootLocationConfigurations?.find(
      (rootLocation) =>
        rootLocationExternalId ===
        (isNewConfigVersion
          ? rootLocation.externalId
          : rootLocation.assetExternalId)
    );
  const configuredSpace =
    dataType === 'app'
      ? configuredRootLocation?.appDataInstanceSpace
      : configuredRootLocation?.sourceDataInstanceSpace;

  useEffect(() => {
    if (isSuccess && configuredRootLocation) {
      if (dataType === 'app' && configuredRootLocation.appDataInstanceSpace) {
        setInstanceSpace(configuredRootLocation.appDataInstanceSpace);
      }
      if (
        dataType === 'source' &&
        configuredRootLocation.sourceDataInstanceSpace
      ) {
        setInstanceSpace(configuredRootLocation.sourceDataInstanceSpace);
      }
    }
  }, [isSuccess, configuredRootLocation, appConfig, dataType]);

  const needsExtraConfirmationStep = Boolean(
    dataType === 'app' && configuredRootLocation?.appDataInstanceSpace
  );

  const handleSave = async () => {
    if (inputValue === configuredSpace) return;
    await saveConfig();
  };

  const saveConfig = async () => {
    if (!appConfig) return;
    const newAppConfig: AppConfig = {
      ...appConfig,
      featureConfiguration: {
        ...appConfig.featureConfiguration,
        rootLocationConfigurations: [
          ...(appConfig.featureConfiguration?.rootLocationConfigurations?.map(
            (rootConfig) => {
              if (
                isNewConfigVersion
                  ? configuredRootLocation?.externalId === rootConfig.externalId
                  : configuredRootLocation?.assetExternalId ===
                    rootConfig.assetExternalId
              ) {
                return dataType === 'app'
                  ? {
                      ...rootConfig,
                      appDataInstanceSpace: instanceSpace,
                    }
                  : {
                      ...rootConfig,
                      sourceDataInstanceSpace: instanceSpace,
                    };
              }
              return { ...rootConfig };
            }
          ) || []),
        ],
      },
    };

    await upsertAppConfig({
      newAppConfig,
    });
    window.location.reload();
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
    if (inputStatus) {
      setInputStatus(undefined);
    }
  };

  useEffect(() => {
    if (enableValidation && status !== 'pending') {
      if (spaces.length > 0 && status === 'success') {
        setInputStatus('success');
        setInputStatusText('Valid space id');
        setInstanceSpace(spaces[0].space);
      } else {
        setInputStatus('critical');
        setInputStatusText('Invalid space id');
      }
    }
  }, [enableValidation, spaces, status]);

  return (
    <S.Wrapper direction="column" gap={32}>
      <Flex direction="column" gap={8}>
        <Body size="x-small" strong>
          {dataType === 'app'
            ? 'AppData instance space'
            : 'SourceData instance space'}
        </Body>
        <Body size="x-small">
          Currently configured space: {configuredSpace || 'none'}.
        </Body>
        <Input
          ref={ref}
          value={inputValue}
          placeholder={configuredSpace || ''}
          disabled={!hasAppConfigPermission}
          onChange={handleChange}
          onBlur={() => setEnableValidation(true)}
          onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Enter') {
              // Press enter to validate spaceId
              ref.current?.blur();
            }
          }}
          onFocus={() => setEnableValidation(false)}
          status={inputStatus}
          statusText={inputStatusText}
        />
      </Flex>
      {dataType === 'source' && (
        <Body size="x-small" strong>
          Make sure you update the transformations in CDF accordingly.
        </Body>
      )}
      <SaveConfigurationButton
        onSave={handleSave}
        isDisabled={
          inputStatus !== 'success' || instanceSpace === configuredSpace
        }
        isLoading={isLoading}
        hasButtonHelpText
        extraContent={
          needsExtraConfirmationStep ? (
            <MigrationConfirmationContent inputValue={inputValue} />
          ) : undefined
        }
      />
    </S.Wrapper>
  );
};
